// Pupil Subjects Tab - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { WeekToggle } from '../../../../lib/components';
import type { WeekType } from '../../../../lib/components';
import { mockClasses, mockSubjects, mockTeachers } from '../../../../lib/mockData';
import { getPeriodsColumnHeader, getSubjectPeriodsValue } from '../../../../lib/utils';
import { useSettingsStore } from '../../../../stores';
import type { Pupil } from '../../../../types';

interface SubjectsTabProps {
  pupil: Pupil;
  onUpdate?: (updates: Partial<Pupil>) => void;
}

const SubjectsTab: FC<SubjectsTabProps> = ({ pupil, onUpdate }) => {
  const { scheduleType } = useSettingsStore();
  const [selectedWeek, setSelectedWeek] = useState<WeekType>('A');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(pupil.subjects);

  const handleOpenAdd = () => {
    setSelectedSubjects(pupil.subjects);
    setAddModalOpen(true);
  };

  const handleToggleSubject = (subjectId: string) => {
    setSelectedSubjects((prev) =>
      prev.includes(subjectId) ? prev.filter((id) => id !== subjectId) : [...prev, subjectId],
    );
  };

  const handleSaveSubjects = () => {
    onUpdate?.({ subjects: selectedSubjects });
    setAddModalOpen(false);
  };
  const pupilSubjects = mockSubjects.filter((s) => pupil.subjects.includes(s.id));
  const pupilClasses = mockClasses.filter((c) => c.pupilIds.includes(pupil.id));

  const coreSubjects = pupilSubjects.filter((s) => s.isCore);
  const optionalSubjects = pupilSubjects.filter((s) => !s.isCore);

  const getTeacherName = (teacherId: string) => {
    const teacher = mockTeachers.find((t) => t.id === teacherId);
    return teacher ? `${teacher.title} ${teacher.lastName}` : 'TBA';
  };

  return (
    <Stack spacing={3}>
      {/* Summary */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="primary.main">
              {pupilSubjects.length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Total Subjects
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="success.main">
              {coreSubjects.length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Subjects
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="secondary.main">
              {optionalSubjects.length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Optional Subjects
            </DsTypography>
          </DsCard>
        </Grid>
      </Grid>

      {/* Core Subjects */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Subjects
        </DsTypography>
        <Grid container spacing={2}>
          {coreSubjects.map((subject) => (
            <Grid item xs={12} sm={6} md={4} key={subject.id}>
              <DsCard
                variant="outlined"
                sx={{
                  p: 2,
                  borderLeft: 8,
                  borderColor: subject.color,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <DsTypography variant="subtitle1" fontWeight={600}>
                      {subject.name}
                    </DsTypography>
                    <DsTypography variant="caption" color="text.secondary">
                      {subject.code} • {subject.department}
                    </DsTypography>
                  </Box>
                  <Chip label="Core" size="small" color="success" />
                </Stack>
                <DsTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {getSubjectPeriodsValue(
                    subject,
                    pupil.yearGroup,
                    scheduleType,
                    scheduleType === 'Bi-Weekly' ? selectedWeek : undefined,
                  )}{' '}
                  {scheduleType === 'Bi-Weekly' ? 'periods/bi-weekly' : 'periods/week'}
                </DsTypography>
              </DsCard>
            </Grid>
          ))}
        </Grid>
      </DsCard>

      {/* Optional Subjects */}
      <DsCard sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <DsTypography variant="h6" fontWeight={600}>
            Optional Subjects
          </DsTypography>
          {onUpdate && (
            <DsButton variant="outlined" size="small" startIcon={<DsIcon icon="PLUS_CIRCLE" />} onClick={handleOpenAdd}>
              Add Subject
            </DsButton>
          )}
        </Stack>
        {optionalSubjects.length > 0 ? (
          <Grid container spacing={2}>
            {optionalSubjects.map((subject) => (
              <Grid item xs={12} sm={6} md={4} key={subject.id}>
                <DsCard
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderLeft: 4,
                    borderColor: subject.color,
                  }}
                >
                  <Box>
                    <DsTypography variant="subtitle1" fontWeight={600}>
                      {subject.name}
                    </DsTypography>
                    <DsTypography variant="caption" color="text.secondary">
                      {subject.code} • {subject.department}
                    </DsTypography>
                  </Box>
                  <DsTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {getSubjectPeriodsValue(
                      subject,
                      pupil.yearGroup,
                      scheduleType,
                      scheduleType === 'Bi-Weekly' ? selectedWeek : undefined,
                    )}{' '}
                    {scheduleType === 'Bi-Weekly' ? 'periods/bi-weekly' : 'periods/week'}
                  </DsTypography>
                </DsCard>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
            <DsTypography color="text.secondary">No optional subjects selected.</DsTypography>
          </Box>
        )}
      </DsCard>

      {/* Class Assignments */}
      <DsCard sx={{ p: 3 }}>
        <DsTypography variant="h6" fontWeight={600} gutterBottom>
          Class Assignments
        </DsTypography>
        {scheduleType === 'Bi-Weekly' && <WeekToggle selectedWeek={selectedWeek} onChange={setSelectedWeek} />}
        {pupilClasses.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Class Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Teacher</TableCell>
                  <TableCell align="center">{getPeriodsColumnHeader(scheduleType)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pupilClasses.map((cls) => {
                  const subject = mockSubjects.find((s) => s.id === cls.subjectId);
                  return (
                    <TableRow key={cls.id} hover>
                      <TableCell>
                        <DsTypography variant="body2" fontWeight={500}>
                          {cls.name}
                        </DsTypography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: subject?.color || '#9E9E9E',
                            }}
                          />
                          <span>{subject?.name || 'Unknown'}</span>
                        </Stack>
                      </TableCell>
                      <TableCell>{getTeacherName(cls.teacherId)}</TableCell>
                      <TableCell align="center">
                        {subject
                          ? getSubjectPeriodsValue(
                              subject,
                              pupil.yearGroup,
                              scheduleType,
                              scheduleType === 'Bi-Weekly' ? selectedWeek : undefined,
                            )
                          : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
            <DsTypography color="text.secondary">No class assignments found.</DsTypography>
          </Box>
        )}
      </DsCard>

      {/* Add/Edit Subjects Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Subjects</DialogTitle>
        <DialogContent>
          <DsTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select optional subjects for this pupil (core subjects are automatically assigned):
          </DsTypography>
          <Stack spacing={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {mockSubjects
              .filter((s) => s.yearGroups.includes(pupil.yearGroup))
              .map((subject) => (
                <Box
                  key={subject.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: selectedSubjects.includes(subject.id) ? 'primary.main' : 'divider',
                    bgcolor: selectedSubjects.includes(subject.id) ? 'primary.light' : 'transparent',
                    cursor: subject.isCore ? 'not-allowed' : 'pointer',
                    opacity: subject.isCore ? 0.7 : 1,
                  }}
                  onClick={() => !subject.isCore && handleToggleSubject(subject.id)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedSubjects.includes(subject.id)}
                        onChange={() => !subject.isCore && handleToggleSubject(subject.id)}
                        disabled={subject.isCore}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: subject.color }} />
                        <span>{subject.name}</span>
                        <Chip label={subject.code} size="small" variant="outlined" />
                        {subject.isCore && <Chip label="Core" size="small" color="success" />}
                      </Stack>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setAddModalOpen(false)}>
            Cancel
          </DsButton>
          <DsButton variant="contained" onClick={handleSaveSubjects}>
            Save ({selectedSubjects.length} subjects)
          </DsButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SubjectsTab;
